import { createContext, useContext, useRef, useState } from "react";
import { FormInteractionProvider } from "../../../Forms/contexts/FormInteractionContext";

const VariableContext = createContext({
  variableName: "",
  setVariableName: () => {},
  isPlaceholder: false,
  setIsPlaceholder: () => {},
  placeholderText: "",
  setPlaceholderText: () => {},
  isDefaultSelection: false,
  setIsDefaultSelection: () => {},
  defaultOptions: [],
  setDefaultOptions: () => {},
  defaultSelection: {},
  setDefaultSelection: () => {},
  isRequired: true,
  setIsRequired: () => {},
  isDefaultAnswer: false,
  setIsDefaultAnswer: () => {},
  defaultValue: "",
  setDefaultValue: () => {},
  isMaxLength: false,
  setIsMaxLength: () => {},
  isMinLength: false,
  setIsMinLength: () => {},
  maxLength: 3,
  setMaxLength: () => {},
  minLength: null,
  setMinLength: () => {},
  chipType: null,
  setChipType: () => {},
  isDuplicateName: false,
  setIsDuplicateName: () => {},
  dateFormat: null,
  setDateFormat: () => {},
  dateSeparator: null,
  setDateSeparator: () => {},
  pastDates: false,
  setPastDates: () => {},
  futureDates: false,
  setFutureDates: () => {},
  startDate: null,
  setStartDate: () => {},
  endDate: null,
  setEndDate: () => {},
  isStartDate: false,
  setIsStartDate: () => {},
  isEndDate: false,
  setIsEndDate: () => {},
  dateFormatOptions: [],
  setDateFormatOptions: () => {},
  isDecimal: false,
  setIsDecimal: () => {},
  onlyPositiveNumbers: false,
  setOnlyPositiveNumbers: () => {},
  isAutomaticallySetValue: false,
  setIsAutomaticallySetValue: () => {},
  expressionValue: "",
  setExpressionValue: () => {},
  optionsView: false,
  setOptionsView: () => {},
  numberOnly: false,
  setNumberOnly: () => {},
  description: "",
  setDescription: () => {},
  isMultiLine: true,
  setIsMultiLine: () => {},
  lineCount: 1,
  setLineCount: () => {},
  showChipName: true,
  setShowChipName: () => {},
  showNumberSpinner: false,
  setShowNumberSpinner: () => {},
  multiSelect: false,
  setMultiSelect: () => {},
  multiDimension: false,
  setMultiDimension: () => {},
  showMultiDimensionChipModal: false,
  setShowMultiDimensionChipModal: () => {},
  skipDimensionOptionsFetchOnModalOpenRef: { current: false },
  adminMultiDimensionModalCloseCallbackRef: { current: null },
});

export const VariableContextProvider = ({ children }) => {
  const [variableName, setVariableName] = useState("");
  const [isRequired, setIsRequired] = useState(false);
  const [isPlaceholder, setIsPlaceholder] = useState(false);
  const [placeholderText, setPlaceholderText] = useState("");
  const [isDefaultSelection, setIsDefaultSelection] = useState(false);
  const [defaultOptions, setDefaultOptions] = useState([]);
  const [defaultSelection, setDefaultSelection] = useState({});
  const [isDefaultAnswer, setIsDefaultAnswer] = useState(false);
  const [defaultValue, setDefaultValue] = useState("");
  const [isMaxLength, setIsMaxLength] = useState(false);
  const [isMinLength, setIsMinLength] = useState(false);
  const [maxLength, setMaxLength] = useState(3);
  const [minLength, setMinLength] = useState(null);
  const [chipType, setChipType] = useState(null);
  const [isDuplicateName, setIsDuplicateName] = useState(false);
  const [dateFormat, setDateFormat] = useState(null);
  const [dateFormatOptions, setDateFormatOptions] = useState([]);
  const [dateSeparator, setDateSeparator] = useState(null);
  const [pastDates, setPastDates] = useState(false);
  const [futureDates, setFutureDates] = useState(false);
  const [isStartDate, setIsStartDate] = useState(false);
  const [isEndDate, setIsEndDate] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isDecimal, setIsDecimal] = useState(false);
  const [onlyPositiveNumbers, setOnlyPositiveNumbers] = useState(false);
  const [optionsView, setOptionsView] = useState(false);
  const [numberOnly, setNumberOnly] = useState(false);
  const [description, setDescription] = useState("");
  const [hasVarChanges, setHasVarChanges] = useState(false);
  const [isAutomaticallySetValue, setIsAutomaticallySetValue] = useState(false);
  const [expressionValue, setExpressionValue] = useState("");
  const [isMultiLine, setIsMultiLine] = useState(true);
  const [lineCount, setLineCount] = useState(1);
  const [showChipName, setShowChipName] = useState(true);
  const [showNumberSpinner, setShowNumberSpinner] = useState(false);
  const [multiSelect, setMultiSelect] = useState(false);
  const [multiDimension, setMultiDimension] = useState(false);
  const [showMultiDimensionChipModal, setShowMultiDimensionChipModal] = useState(false);
  const skipDimensionOptionsFetchOnModalOpenRef = useRef(false);
  /** Set by Admin global chip UI; invoked when multi-dimension modal closes to refresh profile-control API state. */
  const adminMultiDimensionModalCloseCallbackRef = useRef(null);
  const variableValues = {
    variableName,
    setVariableName,
    isPlaceholder,
    setIsPlaceholder,
    isDefaultAnswer,
    setIsDefaultAnswer,
    placeholderText,
    setPlaceholderText,
    defaultValue,
    setDefaultValue,
    isDefaultSelection,
    setIsDefaultSelection,
    defaultSelection,
    setDefaultSelection,
    defaultOptions,
    setDefaultOptions,
    isRequired,
    setIsRequired,
    isMaxLength,
    setIsMaxLength,
    isMinLength,
    setIsMinLength,
    maxLength,
    setMaxLength,
    minLength,
    setMinLength,
    chipType,
    setChipType,
    isDuplicateName,
    setIsDuplicateName,
    dateFormat,
    setDateFormat,
    dateSeparator,
    setDateSeparator,
    pastDates,
    setPastDates,
    futureDates,
    setFutureDates,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    isStartDate,
    setIsStartDate,
    isEndDate,
    setIsEndDate,
    dateFormatOptions,
    setDateFormatOptions,
    isDecimal,
    setIsDecimal,
    onlyPositiveNumbers,
    setOnlyPositiveNumbers,
    optionsView,
    setOptionsView,
    numberOnly,
    setNumberOnly,
    hasVarChanges,
    setHasVarChanges,
    isAutomaticallySetValue,
    setIsAutomaticallySetValue,
    expressionValue,
    setExpressionValue,
    description,
    setDescription,
    isMultiLine,
    setIsMultiLine,
    lineCount,
    setLineCount,
    showChipName,
    setShowChipName,
    showNumberSpinner,
    setShowNumberSpinner,
    multiSelect,
    setMultiSelect,
    multiDimension,
    setMultiDimension,
    showMultiDimensionChipModal,
    setShowMultiDimensionChipModal,
    skipDimensionOptionsFetchOnModalOpenRef,
    adminMultiDimensionModalCloseCallbackRef,
  };
  return (
    <VariableContext.Provider value={variableValues}>
      <FormInteractionProvider>{children}</FormInteractionProvider>
    </VariableContext.Provider>
  );
};

export const useVariablesContext = () => {
  return useContext(VariableContext);
};
